import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useFactory } from '@/core/hooks/useFactory';
import { useTenantStore } from '@/core/hooks/useTenant';
import { fetchTenantMembers } from '@/core/api/tenants';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Badge } from '@/shared/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';
import { Checkbox } from '@/shared/ui/checkbox';
import { ScrollArea } from '@/shared/ui/scroll-area';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/shared/ui/accordion';
import { useToast } from '@/shared/hooks/use-toast';
import { Users, Search, CheckCircle, XCircle, Clock, UserPlus, Shield, MapPin, Building2, Workflow, Cog } from 'lucide-react';

interface LocationAssignment {
  id: string;
  area_id: string | null;
  line_id: string | null;
  station_id: string | null;
}

interface FactoryAssignment {
  id: string;
  user_id: string;
  role: 'supervisor' | 'worker';
  status: 'pending' | 'approved' | 'rejected';
}

interface TenantMember {
  id: string;
  user_id: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  profile: {
    id: string;
    email: string;
    full_name: string | null;
    department: string | null;
    job_title: string | null;
  };
  assignment?: FactoryAssignment | null;
  location_assignments: LocationAssignment[];
}

interface FactoryArea {
  id: string;
  name: string;
}

interface ProductionLine {
  id: string;
  name: string;
  area_id: string;
}

interface Station {
  id: string;
  name: string;
  line_id: string;
}

const ROLE_OPTIONS = [
  { value: 'supervisor', label: 'Supervisor' },
  { value: 'worker', label: 'Worker' },
];

const UserManagement = () => {
  const { selectedTenantId } = useTenantStore();
  const { selectedFactoryId } = useFactory();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [editingUser, setEditingUser] = useState<TenantMember | null>(null);
  const [selectedAreas, setSelectedAreas] = useState<Set<string>>(new Set());
  const [selectedLines, setSelectedLines] = useState<Set<string>>(new Set());
  const [selectedStations, setSelectedStations] = useState<Set<string>>(new Set());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: users = [], isLoading: usersLoading } = useQuery({
    queryKey: ['tenant-members', selectedTenantId, selectedFactoryId],
    queryFn: async () => {
      if (!selectedTenantId) return [];

      const members = await fetchTenantMembers(selectedTenantId);

      if (!selectedFactoryId) {
        return members.map((member) => ({
          ...member,
          assignment: null,
          location_assignments: []
        })) as TenantMember[];
      }

      const { data: assignments, error: assignmentError } = await supabase
        .schema('core')
        .from('user_factory_roles')
        .select('id, user_id, role, status')
        .eq('factory_id', selectedFactoryId);

      if (assignmentError) throw assignmentError;

      const assignmentIds = (assignments || []).map((assignment: FactoryAssignment) => assignment.id);
      const { data: locationAssignments, error: locationError } = await supabase
        .schema('core')
        .from('user_location_assignments')
        .select('id, user_factory_role_id, area_id, line_id, station_id')
        .in('user_factory_role_id', assignmentIds.length ? assignmentIds : ['00000000-0000-0000-0000-000000000000']);

      if (locationError) throw locationError;

      return members.map((member) => {
        const assignment = (assignments || []).find((item: FactoryAssignment) => item.user_id === member.user_id) || null;
        const memberLocations = (locationAssignments || [])
          .filter((assignmentItem: any) => assignmentItem.user_factory_role_id === assignment?.id)
          .map((assignmentItem: any) => ({
            id: assignmentItem.id,
            area_id: assignmentItem.area_id,
            line_id: assignmentItem.line_id,
            station_id: assignmentItem.station_id,
          }));

        return {
          ...member,
          assignment,
          location_assignments: memberLocations,
        } as TenantMember;
      });
    },
    enabled: !!selectedTenantId
  });

  const { data: areas = [] } = useQuery({
    queryKey: ['factory-areas', selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from('factory_areas')
        .select('id, name')
        .eq('factory_id', selectedFactoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as FactoryArea[];
    },
    enabled: !!selectedFactoryId
  });

  const { data: lines = [] } = useQuery({
    queryKey: ['production-lines', selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from('production_lines')
        .select('id, name, area_id')
        .eq('factory_id', selectedFactoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as ProductionLine[];
    },
    enabled: !!selectedFactoryId
  });

  const { data: stations = [] } = useQuery({
    queryKey: ['stations', selectedFactoryId],
    queryFn: async () => {
      if (!selectedFactoryId) return [];
      const { data, error } = await supabase
        .schema('core')
        .from('stations')
        .select('id, name, line_id')
        .eq('factory_id', selectedFactoryId)
        .eq('is_active', true)
        .order('sort_order');
      if (error) throw error;
      return data as Station[];
    },
    enabled: !!selectedFactoryId
  });

  const updateMembershipMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: 'approved' | 'rejected' }) => {
      const { error } = await supabase
        .schema('core')
        .from('tenant_memberships')
        .update({ status })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', selectedTenantId] });
      toast({ title: 'Membership updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Failed to update membership', description: error.message, variant: 'destructive' });
    }
  });

  const updateFactoryRoleMutation = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: 'supervisor' | 'worker' | null }) => {
      if (!selectedTenantId || !selectedFactoryId) return;

      if (!role) {
        const { error } = await supabase
          .schema('core')
          .from('user_factory_roles')
          .delete()
          .eq('user_id', userId)
          .eq('factory_id', selectedFactoryId);

        if (error) throw error;
        return;
      }

      const { error } = await supabase
        .schema('core')
        .from('user_factory_roles')
        .upsert({
          user_id: userId,
          tenant_id: selectedTenantId,
          factory_id: selectedFactoryId,
          role,
          status: 'approved'
        }, { onConflict: 'user_id,factory_id' });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', selectedTenantId, selectedFactoryId] });
      toast({ title: 'Factory role updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Failed to update factory role', description: error.message, variant: 'destructive' });
    }
  });

  const saveLocationsMutation = useMutation({
    mutationFn: async ({ userFactoryRoleId, tenantId, areas, lines, stations }: { 
      userFactoryRoleId: string;
      tenantId: string;
      areas: string[]; 
      lines: string[]; 
      stations: string[];
    }) => {
      const { error: deleteError } = await supabase
        .schema('core')
        .from('user_location_assignments')
        .delete()
        .eq('user_factory_role_id', userFactoryRoleId);

      if (deleteError) throw deleteError;

      const assignments: { user_factory_role_id: string; tenant_id: string; area_id?: string; line_id?: string; station_id?: string }[] = [];

      areas.forEach(areaId => {
        assignments.push({ user_factory_role_id: userFactoryRoleId, tenant_id: tenantId, area_id: areaId });
      });

      lines.forEach(lineId => {
        assignments.push({ user_factory_role_id: userFactoryRoleId, tenant_id: tenantId, line_id: lineId });
      });

      stations.forEach(stationId => {
        assignments.push({ user_factory_role_id: userFactoryRoleId, tenant_id: tenantId, station_id: stationId });
      });

      if (assignments.length > 0) {
        const { error: insertError } = await supabase
          .schema('core')
          .from('user_location_assignments')
          .insert(assignments);

        if (insertError) throw insertError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenant-members', selectedTenantId, selectedFactoryId] });
      toast({ title: 'Locations updated successfully' });
      setEditingUser(null);
    },
    onError: (error) => {
      toast({ title: 'Failed to update locations', description: error.message, variant: 'destructive' });
    }
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.profile?.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.profile?.full_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = users.filter(u => u.status === 'pending').length;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return <Badge variant="default" className="bg-success"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  const getLocationDisplay = (user: TenantMember) => {
    if (!selectedFactoryId || !user.assignment) {
      return <span className="text-muted-foreground">Not assigned</span>;
    }

    const assignments = user.location_assignments || [];

    if (assignments.length === 0) {
      return <span className="text-muted-foreground">All locations</span>;
    }

    const areaCount = assignments.filter(a => a.area_id && !a.line_id && !a.station_id).length;
    const lineCount = assignments.filter(a => a.line_id && !a.station_id).length;
    const stationCount = assignments.filter(a => a.station_id).length;

    const parts = [];
    if (areaCount > 0) parts.push(`${areaCount} area${areaCount > 1 ? 's' : ''}`);
    if (lineCount > 0) parts.push(`${lineCount} line${lineCount > 1 ? 's' : ''}`);
    if (stationCount > 0) parts.push(`${stationCount} station${stationCount > 1 ? 's' : ''}`);

    return <span className="text-sm">{parts.join(', ')}</span>;
  };

  const openLocationDialog = (user: TenantMember) => {
    if (!user.assignment) return;
    setEditingUser(user);

    const areaIds = new Set<string>();
    const lineIds = new Set<string>();
    const stationIds = new Set<string>();

    (user.location_assignments || []).forEach(a => {
      if (a.station_id) stationIds.add(a.station_id);
      else if (a.line_id) lineIds.add(a.line_id);
      else if (a.area_id) areaIds.add(a.area_id);
    });

    setSelectedAreas(areaIds);
    setSelectedLines(lineIds);
    setSelectedStations(stationIds);
  };

  const handleSaveLocations = () => {
    if (!editingUser?.assignment || !selectedTenantId) return;

    saveLocationsMutation.mutate({
      userFactoryRoleId: editingUser.assignment.id,
      tenantId: selectedTenantId,
      areas: Array.from(selectedAreas),
      lines: Array.from(selectedLines),
      stations: Array.from(selectedStations)
    });
  };

  const toggleArea = (areaId: string) => {
    setSelectedAreas(prev => {
      const next = new Set(prev);
      if (next.has(areaId)) {
        next.delete(areaId);
      } else {
        next.add(areaId);
      }
      return next;
    });
  };

  const toggleLine = (lineId: string) => {
    setSelectedLines(prev => {
      const next = new Set(prev);
      if (next.has(lineId)) {
        next.delete(lineId);
      } else {
        next.add(lineId);
      }
      return next;
    });
  };

  const toggleStation = (stationId: string) => {
    setSelectedStations(prev => {
      const next = new Set(prev);
      if (next.has(stationId)) {
        next.delete(stationId);
      } else {
        next.add(stationId);
      }
      return next;
    });
  };

  const getAssignmentCount = () => {
    return selectedAreas.size + selectedLines.size + selectedStations.size;
  };

  if (!selectedTenantId) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="max-w-md">
          <CardHeader className="text-center">
            <Shield className="h-12 w-12 mx-auto text-muted-foreground mb-2" />
            <CardTitle>No Tenant Selected</CardTitle>
            <CardDescription>
              Select a tenant in your profile menu to manage users.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold flex items-center gap-2">
            <Users className="h-5 w-5 md:h-6 md:w-6 text-primary" />
            User Management
          </h1>
          <p className="text-sm text-muted-foreground">Manage tenant memberships and factory roles</p>
        </div>
        {pendingCount > 0 && (
          <Badge variant="secondary" className="text-sm bg-warning text-warning-foreground w-fit">
            {pendingCount} pending approval{pendingCount > 1 ? 's' : ''}
          </Badge>
        )}
      </div>

      {!selectedFactoryId && (
        <Card>
          <CardContent className="py-4 text-sm text-muted-foreground">
            Select a factory to assign roles and locations.
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-[150px]">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {usersLoading ? (
            <div className="text-center py-8 text-muted-foreground">Loading users...</div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <UserPlus className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No users found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="hidden md:table-cell">Department</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead className="hidden lg:table-cell">Locations</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{user.profile?.full_name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{user.profile?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div>
                          <p className="text-sm">{user.profile?.department || '-'}</p>
                          <p className="text-xs text-muted-foreground">{user.profile?.job_title || ''}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Select
                          value={user.assignment?.role ?? '__none__'}
                          onValueChange={(value) => updateFactoryRoleMutation.mutate({ userId: user.user_id, role: value === '__none__' ? null : (value as 'supervisor' | 'worker') })}
                          disabled={!selectedFactoryId || user.status !== 'approved'}
                        >
                          <SelectTrigger className="w-[120px] md:w-[140px]">
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">No role</SelectItem>
                            {ROLE_OPTIONS.map(role => (
                              <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-auto py-1 px-2"
                          onClick={() => openLocationDialog(user)}
                          disabled={!selectedFactoryId || !user.assignment || user.status !== 'approved'}
                        >
                          <MapPin className="h-3 w-3 mr-1 text-muted-foreground" />
                          {getLocationDisplay(user)}
                        </Button>
                      </TableCell>
                      <TableCell>{getStatusBadge(user.status)}</TableCell>
                      <TableCell className="text-right">
                        {user.status === 'pending' ? (
                          <div className="flex justify-end gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() => updateMembershipMutation.mutate({ id: user.id, status: 'approved' })}
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Approve</span>
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => updateMembershipMutation.mutate({ id: user.id, status: 'rejected' })}
                            >
                              <XCircle className="h-4 w-4 mr-1" />
                              <span className="hidden sm:inline">Reject</span>
                            </Button>
                          </div>
                        ) : (
                          <Button
                            size="sm"
                            variant="outline"
                            className="lg:hidden"
                            onClick={() => openLocationDialog(user)}
                            disabled={!selectedFactoryId || !user.assignment}
                          >
                            <MapPin className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editingUser} onOpenChange={(open) => !open && setEditingUser(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Locations</DialogTitle>
            <DialogDescription>
              Select the areas, lines, and stations where {editingUser?.profile?.full_name || 'this user'} will work.
              Leave all unchecked for access to all locations.
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="max-h-[400px] pr-4">
            <Accordion type="multiple" defaultValue={['areas', 'lines', 'stations']} className="w-full">
              <AccordionItem value="areas">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span>Areas</span>
                    {selectedAreas.size > 0 && (
                      <Badge variant="secondary" className="ml-2">{selectedAreas.size}</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pl-6">
                    {areas.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No areas defined</p>
                    ) : (
                      areas.map(area => (
                        <div key={area.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={`area-${area.id}`}
                            checked={selectedAreas.has(area.id)}
                            onCheckedChange={() => toggleArea(area.id)}
                          />
                          <label
                            htmlFor={`area-${area.id}`}
                            className="text-sm cursor-pointer"
                          >
                            {area.name}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="lines">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Workflow className="h-4 w-4 text-primary" />
                    <span>Production Lines</span>
                    {selectedLines.size > 0 && (
                      <Badge variant="secondary" className="ml-2">{selectedLines.size}</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pl-6">
                    {lines.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No lines defined</p>
                    ) : (
                      areas.map(area => {
                        const areaLines = lines.filter(line => line.area_id === area.id);
                        if (areaLines.length === 0) return null;
                        return (
                          <div key={area.id} className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">{area.name}</p>
                            {areaLines.map(line => (
                              <div key={line.id} className="flex items-center space-x-2 ml-2">
                                <Checkbox
                                  id={`line-${line.id}`}
                                  checked={selectedLines.has(line.id)}
                                  onCheckedChange={() => toggleLine(line.id)}
                                />
                                <label
                                  htmlFor={`line-${line.id}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {line.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="stations">
                <AccordionTrigger className="hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Cog className="h-4 w-4 text-primary" />
                    <span>Stations</span>
                    {selectedStations.size > 0 && (
                      <Badge variant="secondary" className="ml-2">{selectedStations.size}</Badge>
                    )}
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 pl-6">
                    {stations.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No stations defined</p>
                    ) : (
                      lines.map(line => {
                        const lineStations = stations.filter(station => station.line_id === line.id);
                        if (lineStations.length === 0) return null;
                        const area = areas.find(area => area.id === line.area_id);
                        return (
                          <div key={line.id} className="space-y-1">
                            <p className="text-xs font-medium text-muted-foreground">
                              {area?.name} → {line.name}
                            </p>
                            {lineStations.map(station => (
                              <div key={station.id} className="flex items-center space-x-2 ml-2">
                                <Checkbox
                                  id={`station-${station.id}`}
                                  checked={selectedStations.has(station.id)}
                                  onCheckedChange={() => toggleStation(station.id)}
                                />
                                <label
                                  htmlFor={`station-${station.id}`}
                                  className="text-sm cursor-pointer"
                                >
                                  {station.name}
                                </label>
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </ScrollArea>

          <div className="flex items-center justify-between pt-4 border-t">
            <span className="text-sm text-muted-foreground">
              {getAssignmentCount() === 0 ? 'Access to all locations' : `${getAssignmentCount()} location(s) selected`}
            </span>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditingUser(null)}>Cancel</Button>
              <Button onClick={handleSaveLocations} disabled={saveLocationsMutation.isPending}>
                {saveLocationsMutation.isPending ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
